export const businessEthicsCourse = {
  id: 'business-ethics',
  title: 'Business Ethics',
  description: 'Comprehensive study of ethical principles in business, corporate social responsibility, stakeholder management, and ethical decision-making frameworks for modern business challenges.',
  status: 'published',
  duration: '15 weeks',
  level: 'Intermediate',
  credits: 3,
  category: 'Business',
  prerequisites: ['Introduction to Business', 'Principles of Management'],
  learningObjectives: [
    'Understand fundamental ethical theories and their business applications',
    'Analyze corporate social responsibility and stakeholder management',
    'Evaluate ethical dilemmas using decision-making frameworks',
    'Examine environmental sustainability and business practices',
    'Assess global business ethics and cultural considerations',
    'Develop personal and organizational ethical leadership skills'
  ],
  skills: [
    'Ethical Decision Making',
    'Stakeholder Analysis',
    'Corporate Social Responsibility',
    'Sustainability Management',
    'Ethics Policy Development',
    'Risk Assessment',
    'Compliance Management',
    'Ethical Leadership',
    'Crisis Management',
    'Reputation Management'
  ],
  careerPaths: [
    'Ethics and Compliance Officer',
    'Corporate Social Responsibility Manager',
    'Sustainability Manager',
    'Risk Management Specialist',
    'Human Resources Manager',
    'Corporate Communications Manager',
    'Non-Profit Organization Management',
    'Government Relations Specialist',
    'Management Consultant',
    'Internal Auditor'
  ],
  averageSalary: '$60,000 - $120,000',
  marketDemand: 'Growing',
  certifications: [
    'Certified Compliance and Ethics Professional (CCEP)',
    'Certified in Risk and Information Systems Control (CRISC)',
    'Society for Corporate Compliance and Ethics (SCCE)',
    'International Association of Privacy Professionals (IAPP)',
    'Global Reporting Initiative (GRI) Certified'
  ],
  modules: [
    {
      id: 'module-1',
      title: 'Foundations of Business Ethics',
      week: 1,
      description: 'Ethical theories, moral reasoning, and business ethics frameworks',
      learningObjectives: [
        'Understand and apply major ethical theories to business situations',
        'Develop moral reasoning skills for complex business decisions',
        'Analyze stakeholder interests and ethical implications',
        'Create ethical decision-making frameworks for organizations'
      ],
      content: {
        type: 'lesson',
        data: {
          title: 'Ethical Foundations and Business Applications',
          content: `# Ethical Foundations and Business Applications

## Introduction to Business Ethics

### Definition and Scope
Business ethics is the study of appropriate business policies and practices regarding potentially controversial subjects including corporate governance, insider trading, bribery, discrimination, corporate social responsibility, and fiduciary responsibilities. It encompasses the moral principles that guide behavior in the world of business.

**Key Domains of Business Ethics:**
- Individual ethics in business decisions
- Organizational ethical climate and culture
- Professional ethics and codes of conduct
- Corporate social responsibility and sustainability
- Stakeholder relationship management
- Global business ethics and cultural considerations

### Why Business Ethics Matters

**Economic Impact**
- Ethical failures cost billions annually (Enron: $74 billion, Wells Fargo scandal: $3+ billion)
- Consumer trust drives purchase decisions (87% of consumers will purchase from companies that support issues they care about)
- Employee retention improves with strong ethical culture (turnover 40% lower in ethical companies)
- Investment flows increasingly consider ESG factors ($35 trillion in global sustainable investments)

**Legal and Regulatory Drivers**
- Sarbanes-Oxley Act requirements for corporate governance
- Foreign Corrupt Practices Act enforcement
- GDPR privacy regulations
- Increasing government oversight and penalties

**Reputation and Brand Value**
- 86% of global consumers expect companies to take a stand on social issues
- Ethical crises can destroy decades of brand building overnight
- Social media amplifies ethical failures globally and instantly
- Stakeholder activism holds companies accountable

## Major Ethical Theories and Frameworks

### 1. Consequentialism and Utilitarian Ethics

**Foundation and Principles**
Developed by philosophers Jeremy Bentham and John Stuart Mill, utilitarian ethics judges actions based solely on their consequences. The central principle is the "Greatest Happiness Principle" - actions are right when they promote happiness and wrong when they produce the opposite.

**Core Concepts:**
- **Utility Maximization**: Choose actions that produce the greatest good for the greatest number
- **Hedonistic Calculus**: Quantitative approach to measuring pleasure vs. pain
- **Rule vs. Act Utilitarianism**: Following beneficial rules vs. evaluating each act individually
- **Preference Utilitarianism**: Maximizing preference satisfaction rather than just pleasure

**Business Applications:**
*Cost-Benefit Analysis Example*: A pharmaceutical company deciding drug pricing
- Benefits: Corporate profits, investor returns, R&D funding for future drugs
- Costs: Patient access barriers, health outcomes, social welfare impact
- Utilitarian calculation: Price level that maximizes overall social welfare

*Environmental Decision Example*: Factory pollution controls
- Installation cost: $10 million
- Environmental benefit: Reduced health costs, ecosystem protection
- Community impact: Cleaner air, water quality improvement
- Utilitarian approach: Install if total benefits exceed costs

**Limitations and Criticisms:**
- **Measurement Challenges**: How do you quantify and compare different types of benefits and harms?
- **Distribution Issues**: Doesn't address who receives benefits vs. who bears costs
- **Minority Rights**: Can justify harming minorities if majority benefits
- **Practical Implementation**: Difficult to predict all consequences of actions

### 2. Deontological Ethics (Duty-Based Ethics)

**Immanuel Kant's Framework**
Deontological ethics, primarily developed by Kant, focuses on the inherent rightness or wrongness of actions regardless of consequences. Actions are moral if they conform to moral rules or duties.

**Categorical Imperative - Three Formulations:**

**First Formulation - Universal Law**: "Act only according to that maxim whereby you can at the same time will that it should become a universal law."
- Test: Could everyone do this action?
- Example: Lying in business - if everyone lied, business relationships would collapse
- Application: Universal policies that apply to all employees equally

**Second Formulation - Humanity**: "Act so that you treat humanity, whether in your own person or in that of another, always as an end and never merely as a means."
- Respect for human dignity and autonomy
- Prohibition against manipulation and exploitation
- Example: Employee layoffs must consider human impact, not just cost reduction

**Third Formulation - Kingdom of Ends**: Act as if you were legislating for a moral community
- Consider yourself as both subject and sovereign of moral laws
- Collective responsibility for ethical standards
- Application: Creating organizational policies you'd want applied universally

**Business Applications:**
*Truth-Telling and Honesty*
- Advertising must be truthful regardless of competitive pressure
- Financial reporting accuracy is a duty regardless of market reaction
- Product safety disclosures even if they harm sales

*Promise-Keeping and Contracts*
- Honoring agreements even when circumstances change
- Supplier relationship integrity
- Employee commitment fulfillment

*Respect for Persons*
- Informed consent in all business relationships
- Worker safety as categorical imperative
- Customer privacy protection as duty

**Rights-Based Ethics**
Extension of deontological thinking focusing on fundamental human rights that must be respected:

- **Right to Life**: Product safety, workplace safety, environmental protection
- **Right to Liberty**: Freedom of choice, non-coercion in business relationships
- **Right to Property**: Intellectual property protection, fair compensation
- **Right to Privacy**: Data protection, surveillance limitations
- **Right to Fair Treatment**: Non-discrimination, equal opportunity

### 3. Virtue Ethics

**Aristotelian Foundation**
Virtue ethics, tracing back to Aristotle, focuses on character rather than actions or consequences. It asks "What kind of person should I be?" rather than "What should I do?"

**Core Concepts:**
- **Eudaimonia**: Human flourishing or well-being as the ultimate goal
- **Virtues**: Character traits that enable human flourishing
- **Golden Mean**: Virtues as balance between extremes of excess and deficiency
- **Practical Wisdom (Phronesis)**: Ability to discern the right action in particular circumstances

**Cardinal Virtues in Business:**

**Integrity**
- Consistency between values, words, and actions
- Wholeness and authenticity in business relationships
- Example: Patagonia's commitment to environmental values across all operations

**Honesty**
- Truthfulness in communication and representation
- Transparency in business dealings
- Example: Johnson & Johnson's Tylenol recall - immediate honest communication

**Fairness**
- Just treatment of all stakeholders
- Equitable distribution of benefits and burdens
- Example: Costco's employee compensation philosophy vs. industry norms

**Courage**
- Moral courage to do right despite pressure
- Speaking truth to power
- Example: Whistleblowing in cases of corporate wrongdoing

**Temperance**
- Moderation and self-control
- Avoiding excess in pursuit of profit
- Example: Executive compensation restraint during employee layoffs

**Prudence (Practical Wisdom)**
- Sound judgment in complex situations
- Long-term thinking and stakeholder consideration
- Example: Investment in employee development during economic downturns

**Virtue Ethics in Organizational Context:**

*Character-Based Leadership*
- Leaders as moral exemplars setting organizational tone
- Hiring and promotion based on character as well as competence
- Leadership development programs emphasizing virtue development

*Organizational Culture Development*
- Creating systems that reward virtuous behavior
- Storytelling that celebrates virtue exemplars
- Mentoring relationships that develop character

*Professional Virtue Development*
- Industry-specific virtue codes (medical ethics, legal ethics, accounting ethics)
- Continuing education in ethical reasoning
- Peer accountability systems

### 4. Care Ethics and Relationship-Based Approaches

**Foundation and Principles**
Developed primarily by feminist philosophers like Carol Gilligan and Nel Noddings, care ethics emphasizes relationships, context, and emotional intelligence in moral reasoning.

**Key Concepts:**
- **Relational Ontology**: Understanding persons as fundamentally interconnected
- **Contextual Reasoning**: Moral decisions must consider specific relationships and circumstances
- **Emotional Engagement**: Feelings and empathy as legitimate moral guides
- **Responsibility and Response**: Obligation to respond to needs within relationships

**Business Applications:**
*Stakeholder Relationship Management*
- Long-term relationship building over transactional interactions
- Understanding stakeholder needs and vulnerabilities
- Responsive adaptation to changing stakeholder circumstances

*Employee Relations*
- Work-life balance policies that recognize whole person needs
- Flexible policies accommodating individual circumstances
- Mentoring and development as care practices

*Customer Care*
- Service recovery that goes beyond minimum requirements
- Product design considering user vulnerability and needs
- Customer education and protection

## Ethical Decision-Making Frameworks

### Comprehensive Stakeholder Analysis Framework

**Step 1: Stakeholder Identification and Mapping**

*Primary Stakeholders* (Direct impact and influence):
- Shareholders/Owners
- Employees and unions
- Customers and consumers
- Suppliers and business partners
- Local communities

*Secondary Stakeholders* (Indirect but significant interest):
- Government and regulators
- Industry associations
- Environmental groups
- Media and public opinion
- Future generations

*Stakeholder Characteristics Assessment*:
- **Power**: Ability to influence organizational decisions
- **Legitimacy**: Valid claim on organizational attention
- **Urgency**: Time-critical nature of stakeholder claims
- **Proximity**: Geographic and relational closeness to organization

**Step 2: Interest and Impact Analysis**

*For each stakeholder group, analyze*:
- What are their primary interests and concerns?
- How will they be affected by potential decisions?
- What is their capacity to affect organizational success?
- What are their ethical expectations and standards?
- How do their interests align or conflict with other stakeholders?

**Step 3: Ethical Assessment Matrix**

Create matrix evaluating potential decisions against:
- Harm/benefit to each stakeholder group
- Rights respected or violated
- Fairness and justice considerations
- Long-term sustainability implications
- Cultural and contextual appropriateness

### Integrated Ethical Decision-Making Model

**Phase 1: Problem Recognition and Definition**
- Identify ethical dimensions of the situation
- Distinguish ethical issues from legal, economic, or strategic concerns
- Clarify competing values and interests at stake
- Assess urgency and scope of decision required

**Phase 2: Information Gathering and Analysis**
- Collect relevant facts and data
- Identify missing information and assumptions
- Consult relevant policies, codes, and standards
- Seek diverse perspectives and expertise

**Phase 3: Alternative Generation**
- Brainstorm multiple potential solutions
- Consider both obvious and creative alternatives
- Ensure alternatives address root causes, not just symptoms
- Include "do nothing" as one alternative for comparison

**Phase 4: Evaluation Using Multiple Ethical Lenses**

*Consequentialist Analysis*:
- Predict likely outcomes of each alternative
- Quantify costs and benefits where possible
- Consider short-term and long-term consequences
- Assess probability and magnitude of impacts

*Deontological Analysis*:
- Identify duties and rights at stake
- Apply universalizability test
- Check consistency with moral rules and principles
- Ensure respect for human dignity

*Virtue Analysis*:
- Consider what a virtuous person/organization would do
- Evaluate character implications of each alternative
- Assess alignment with organizational values
- Consider role model effects

**Phase 5: Decision and Implementation**
- Select alternative that best satisfies ethical criteria
- Develop implementation plan addressing potential barriers
- Communicate decision and rationale transparently
- Establish monitoring and adjustment mechanisms

**Phase 6: Evaluation and Learning**
- Monitor actual outcomes vs. predicted impacts
- Assess stakeholder responses and satisfaction
- Identify lessons learned for future decisions
- Adjust policies and procedures based on experience

### Case Study Application: Data Privacy and AI Ethics

**Scenario**: A social media company must decide whether to use AI algorithms that increase user engagement but may promote addiction and mental health issues, particularly among teenagers.

**Stakeholder Analysis**:
- **Users**: Want engaging content but also mental health protection
- **Shareholders**: Expect profit maximization and growth
- **Employees**: Pride in work vs. concern about societal impact
- **Regulators**: Increasing pressure for platform accountability
- **Society**: Concerns about democratic discourse and youth development

**Ethical Framework Application**:

*Utilitarian Analysis*:
- Benefits: User satisfaction, advertising revenue, economic growth
- Costs: Mental health impacts, societal polarization, regulatory backlash
- Calculation complexity: How to weigh individual vs. societal costs?

*Deontological Analysis*:
- Duty to protect vulnerable users (especially minors)
- Respect for user autonomy and informed consent
- Truthfulness in algorithm design and disclosure

*Virtue Analysis*:
- What would a responsible technology company do?
- Character implications of prioritizing engagement over well-being
- Role model effects for industry practices

**Decision Framework Outcome**:
Integration of ethical considerations into algorithm design:
- Implement well-being indicators alongside engagement metrics
- Provide user controls and transparency about content curation
- Invest in research on positive technology design
- Collaborate with mental health experts and regulators

This comprehensive approach demonstrates how multiple ethical frameworks can work together to address complex business decisions while maintaining stakeholder trust and organizational integrity.`
        }
      },
      interactiveElements: [
        {
          type: 'finance_interactive',
          title: 'Ethical Decision Matrix',
          description: 'Analyze ethical dilemmas using structured decision-making frameworks',
          exerciseData: {
            type: 'ethical_analysis',
            scenario: 'Corporate Layoff Decision',
            stakeholders: ['employees', 'shareholders', 'customers', 'community'],
            frameworks: ['utilitarian', 'deontological', 'virtue_ethics']
          }
        }
      ]
    },
    {
      id: 'module-2',
      title: 'Corporate Social Responsibility',
      week: 4,
      description: 'CSR strategies, stakeholder capitalism, and sustainable business practices',
      learningObjectives: [
        'Analyze the evolution and components of corporate social responsibility',
        'Evaluate different approaches to stakeholder capitalism and management',
        'Design sustainable business practices and ESG strategies',
        'Assess the business case for social responsibility and sustainability'
      ],
      content: {
        type: 'lesson',
        data: {
          title: 'Corporate Social Responsibility and Stakeholder Management',
          content: `# Corporate Social Responsibility and Stakeholder Management

## Evolution and Definition of Corporate Social Responsibility

### Historical Development

**Pre-1950s: Philanthropy Era**
- Business responsibility limited to profit generation
- Voluntary charitable giving by wealthy business owners
- Paternalistic approach to community relations
- Example: Andrew Carnegie's Gospel of Wealth philosophy

**1950s-1970s: Social Responsibility Emergence**
- Howard Bowen's "Social Responsibilities of the Businessman" (1953)
- Recognition of business impact on society
- Debate between profit maximization vs. social obligation
- Milton Friedman's counterargument: "The business of business is business"

**1980s-1990s: Stakeholder Theory Development**
- R. Edward Freeman's stakeholder theory (1984)
- Recognition of multiple constituencies beyond shareholders
- Corporate citizenship concept emergence
- Environmental movement influence on business practices

**2000s-Present: Integrated Sustainability**
- Triple bottom line framework (People, Planet, Profit)
- ESG (Environmental, Social, Governance) investing
- Shared value creation (Michael Porter & Mark Kramer)
- Sustainable Development Goals alignment

### Modern CSR Definition
Corporate Social Responsibility is a business approach that contributes to sustainable development by delivering economic, social, and environmental benefits for all stakeholders. It goes beyond legal compliance to integrate social and environmental concerns into business operations and stakeholder interactions.

## Carroll's CSR Pyramid: Comprehensive Framework

### Economic Responsibility (Foundation Level)

**Profit Generation and Economic Performance**
- Primary obligation to be profitable and economically viable
- Efficient resource utilization and waste minimization
- Job creation and economic contribution to society
- Innovation and productivity improvements

**Stakeholder Economic Value Creation**
- Fair returns to shareholders and investors
- Competitive wages and benefits to employees
- Value delivery to customers through quality products/services
- Economic development in operating communities

**Case Example: Unilever's Sustainable Living Plan**
- €52 billion revenue while reducing environmental footprint
- Sustainable brands growing 69% faster than rest of business
- 1.3 billion people reached with health and hygiene programs
- Demonstrates economic success through social responsibility

### Legal Responsibility (Compliance Level)

**Regulatory Compliance and Governance**
- Adherence to all applicable laws and regulations
- Robust compliance programs and monitoring systems
- Ethical business conduct and anti-corruption measures
- Transparent reporting and accountability mechanisms

**Industry Standards and Best Practices**
- Following industry-specific regulations and standards
- Participation in voluntary certification programs
- Compliance with international standards (ISO, SA8000, etc.)
- Adoption of best practice frameworks

**Legal Risk Management**
- Proactive identification and mitigation of legal risks
- Regular compliance auditing and assessment
- Employee training on legal and regulatory requirements
- Whistleblower protection and reporting mechanisms

**Case Example: Siemens Compliance Transformation**
- $1.6 billion corruption scandal in 2008
- Complete overhaul of compliance systems and culture
- Investment of over €1 billion in compliance infrastructure
- Now recognized as industry leader in anti-corruption practices

### Ethical Responsibility (Voluntary Level)

**Beyond Legal Minimums**
- Doing what is right, just, and fair even when not required by law
- Anticipating and exceeding societal expectations
- Voluntary adoption of higher standards than legally mandated
- Proactive approach to emerging ethical issues

**Stakeholder Rights and Fair Treatment**
- Respect for human rights throughout operations and supply chain
- Fair labor practices and workplace conditions
- Privacy protection and data security
- Honest and transparent communication

**Ethical Leadership and Culture**
- Tone at the top setting ethical expectations
- Ethics training and awareness programs
- Reward systems aligned with ethical behavior
- Decision-making processes incorporating ethical considerations

**Case Example: Patagonia's Ethical Stance**
- "Don't Buy This Jacket" campaign reducing unnecessary consumption
- 1% for the Planet giving pledge
- Fair Trade certification for supply chain
- Activism on environmental and social issues

### Philanthropic Responsibility (Discretionary Level)

**Community Investment and Development**
- Strategic philanthropy aligned with business capabilities
- Community development programs and partnerships
- Educational initiatives and capacity building
- Disaster relief and humanitarian assistance

**Voluntary Giving and Social Programs**
- Corporate foundation establishment and management
- Employee volunteer programs and paid time off for community service
- Matching gift programs and payroll deduction giving
- Pro bono services and skill-based volunteering

**Arts, Culture, and Society Support**
- Cultural institution sponsorship and support
- Arts education and accessibility programs
- Historical preservation and cultural heritage protection
- Sports and recreation facility development

**Case Example: Salesforce's Philanthropic Model**
- 1-1-1 model: 1% of equity, product, and employee time
- $240+ million in grants since inception
- 4 million+ volunteer hours by employees
- Integration of giving into core business strategy

## Stakeholder Capitalism and Management

### Stakeholder Theory Fundamentals

**Freeman's Stakeholder Definition**
"Any group or individual who can affect or is affected by the achievement of the organization's objectives."

**Key Principles:**
- Stakeholders have legitimate interests in organizational activities
- Stakeholder interests have intrinsic value, not just instrumental value
- Managers must consider stakeholder impacts in decision-making
- Long-term success requires stakeholder relationship management

### Stakeholder Identification and Mapping

**Primary Stakeholders (High Influence, High Interest)**
- **Shareholders/Investors**: Financial returns, growth, governance
- **Employees**: Job security, compensation, working conditions, development
- **Customers**: Product quality, value, service, safety
- **Suppliers**: Fair payment, long-term relationships, capacity building
- **Communities**: Economic development, environmental protection, social investment

**Secondary Stakeholders (Variable Influence and Interest)**
- **Government/Regulators**: Compliance, tax revenue, policy cooperation
- **NGOs/Activists**: Social and environmental advocacy
- **Media**: Transparency, newsworthy stories, corporate reputation
- **Competitors**: Industry standards, fair competition
- **Future Generations**: Sustainability, resource preservation

### Stakeholder Engagement Strategies

**Engagement Hierarchy**
1. **Inform**: One-way communication providing information
2. **Consult**: Two-way communication seeking feedback
3. **Involve**: Working together on solutions
4. **Collaborate**: Partnering in decision-making
5. **Empower**: Stakeholder-led initiatives with company support

**Best Practices for Stakeholder Engagement**
- Regular, systematic communication and feedback mechanisms
- Materiality assessment to identify priority issues and stakeholders
- Multi-stakeholder platforms and collaborative initiatives
- Transparent reporting on stakeholder engagement outcomes
- Responsiveness to stakeholder concerns and adaptation of practices

**Case Example: Interface Inc.'s Mission Zero**
- Engaged stakeholders in setting science-based climate targets
- Collaborated with suppliers on renewable energy transitions
- Partnered with communities on carbon sequestration projects
- Achieved carbon neutral manufacturing ahead of schedule

## Triple Bottom Line and ESG Integration

### People (Social Bottom Line)

**Employee Well-being and Development**
- Health and safety programs exceeding regulatory requirements
- Diversity, equity, and inclusion initiatives
- Skills development and career advancement opportunities
- Work-life balance and flexible work arrangements

**Community Impact and Development**
- Local hiring and supplier preferences
- Education and training programs for community members
- Healthcare access and nutrition programs
- Infrastructure development and improvement

**Human Rights and Labor Standards**
- Supply chain human rights due diligence
- Living wage initiatives and fair compensation
- Freedom of association and collective bargaining rights
- Child labor and forced labor prevention programs

### Planet (Environmental Bottom Line)

**Climate Change Mitigation**
- Science-based emission reduction targets
- Renewable energy adoption and efficiency improvements
- Carbon offset and sequestration programs
- Climate risk assessment and adaptation strategies

**Resource Conservation and Circular Economy**
- Water stewardship and conservation programs
- Waste reduction and recycling initiatives
- Sustainable sourcing and procurement practices
- Product design for durability and repairability

**Biodiversity Protection**
- Ecosystem conservation and restoration projects
- Sustainable agriculture and forestry practices
- Pollution prevention and remediation
- Wildlife habitat protection and corridors

### Profit (Economic Bottom Line)

**Sustainable Financial Performance**
- Long-term value creation over short-term gains
- Integration of sustainability costs and benefits in financial planning
- Risk management through environmental and social initiatives
- Innovation driving new revenue streams and market opportunities

**Shared Value Creation**
- Business strategies that create both economic and social value
- Market-based solutions to social and environmental challenges
- Stakeholder partnerships generating mutual benefits
- Inclusive business models serving base-of-pyramid markets

## ESG (Environmental, Social, Governance) Framework

### Environmental Criteria

**Climate and Carbon Management**
- Scope 1, 2, and 3 greenhouse gas emissions measurement and reduction
- Climate scenario planning and risk assessment
- Renewable energy transition and energy efficiency
- Carbon pricing and internal carbon markets

**Resource Management**
- Water use efficiency and quality protection
- Waste management and circular economy principles
- Sustainable materials and packaging
- Land use and biodiversity impact assessment

### Social Criteria

**Workforce Management**
- Employee engagement and satisfaction measurement
- Diversity and inclusion metrics and targets
- Health and safety performance indicators
- Training and development investment per employee

**Community Relations**
- Community investment as percentage of revenue
- Local procurement and hiring preferences
- Social impact measurement and reporting
- Stakeholder grievance and feedback mechanisms

### Governance Criteria

**Board Composition and Independence**
- Board diversity and expertise requirements
- Independent director majority and leadership
- Regular board evaluation and renewal processes
- Executive compensation aligned with ESG performance

**Business Ethics and Transparency**
- Code of conduct and ethics training programs
- Anti-corruption and anti-bribery policies
- Whistleblower protection and reporting systems
- Transparent communication and stakeholder engagement

## Business Case for CSR and Sustainability

### Financial Performance Benefits

**Revenue Growth Opportunities**
- Sustainable products commanding premium pricing (average 15-20% price premium)
- New market access through sustainability credentials
- Customer loyalty and retention improvements
- Brand differentiation in competitive markets

**Cost Reduction and Efficiency**
- Energy and resource efficiency reducing operational costs
- Waste reduction and circular economy savings
- Supply chain optimization and risk reduction
- Employee productivity and retention improvements

**Risk Management and Resilience**
- Regulatory compliance reducing legal and financial risks
- Reputation protection through proactive CSR
- Supply chain disruption mitigation
- Climate change adaptation and resilience building

### Access to Capital and Investment

**ESG Investing Growth**
- $35 trillion in global sustainable investments (2020)
- 86% of millennials interested in sustainable investing
- ESG factors influencing credit ratings and loan terms
- Green bonds and sustainability-linked financing options

**Stakeholder Pressure and Expectations**
- Institutional investor engagement on ESG issues
- Customer preferences driving sustainable product demand
- Employee attraction and retention through purpose-driven work
- Regulatory requirements for ESG disclosure and reporting

### Competitive Advantage and Innovation

**Innovation Driver**
- Sustainability challenges spurring technological innovation
- Circular economy creating new business models
- Collaboration with stakeholders generating new solutions
- First-mover advantage in sustainable markets

**Operational Excellence**
- Continuous improvement culture through sustainability focus
- Stakeholder engagement improving decision-making quality
- Systems thinking approach enhancing organizational learning
- Purpose-driven culture improving employee engagement

**Case Study: Unilever's Sustainable Living Brands**
- Sustainable brands growing 69% faster than traditional brands
- €1 billion revenue from sustainable living brands in 2018
- Purpose-driven brands attracting and retaining talent
- Innovation pipeline focused on sustainable solutions

This comprehensive approach to CSR and stakeholder management demonstrates how organizations can create shared value while building sustainable competitive advantage and contributing positively to society and the environment.`
        }
      },
      interactiveElements: [
        {
          type: 'finance_interactive',
          title: 'CSR Impact Calculator',
          description: 'Evaluate corporate social responsibility initiatives and their financial impact',
          exerciseData: {
            type: 'csr_analysis',
            scenario: 'Sustainability Program Investment',
            metrics: ['cost', 'social_impact', 'environmental_benefit', 'reputation_value']
          }
        }
      ]
    },
    {
      id: 'module-3',
      title: 'Ethics in Global Business',
      week: 7,
      description: 'International business ethics, cultural considerations, and global compliance',
      content: {
        type: 'lesson',
        data: {
          title: 'Global Business Ethics and Cultural Considerations',
          content: `# Global Business Ethics and Cultural Considerations

## International Business Ethics

### Cultural Relativism vs. Universalism
- **Cultural Relativism**: Ethics vary by culture
- **Ethical Universalism**: Universal moral principles
- **Pragmatic Approach**: Balanced cultural sensitivity
- **Core Values**: Non-negotiable ethical standards

### Global Compliance Frameworks
- **Foreign Corrupt Practices Act (FCPA)**: US anti-bribery law
- **UK Bribery Act**: Comprehensive anti-corruption
- **OECD Guidelines**: Multinational enterprise standards
- **UN Global Compact**: Universal principles

## Cross-Cultural Ethics

### Hofstede's Cultural Dimensions
- **Power Distance**: Hierarchy acceptance
- **Individualism vs. Collectivism**: Group orientation
- **Uncertainty Avoidance**: Risk tolerance
- **Long-term Orientation**: Future focus

### Ethical Challenges
- **Gift-Giving Practices**: Cultural vs. corruption
- **Labor Standards**: Different country requirements
- **Environmental Regulations**: Varying standards
- **Human Rights**: Universal vs. local practices`
        }
      },
      interactiveElements: [
        {
          type: 'finance_interactive',
          title: 'Global Ethics Simulator',
          description: 'Navigate ethical dilemmas in international business contexts',
          exerciseData: {
            type: 'global_ethics',
            scenario: 'International Joint Venture Ethics',
            cultures: ['western', 'asian', 'middle_eastern', 'latin_american']
          }
        }
      ]
    },
    {
      id: 'module-4',
      title: 'Organizational Ethics and Compliance',
      week: 11,
      description: 'Ethics programs, compliance systems, and organizational culture development',
      content: {
        type: 'lesson',
        data: {
          title: 'Building Ethical Organizations and Compliance Systems',
          content: `# Building Ethical Organizations and Compliance Systems

## Ethics Programs

### Program Components
- **Code of Ethics**: Written standards and expectations
- **Ethics Training**: Regular education programs
- **Reporting Systems**: Anonymous reporting mechanisms
- **Investigation Procedures**: Fair and thorough processes

### Compliance Framework
- **Risk Assessment**: Identifying ethical risks
- **Policy Development**: Clear guidelines and procedures
- **Monitoring Systems**: Regular compliance audits
- **Corrective Actions**: Response to violations

## Organizational Culture

### Ethical Climate
- **Leadership Tone**: Top-down ethical messaging
- **Cultural Values**: Embedded ethical principles
- **Reward Systems**: Incentivizing ethical behavior
- **Communication**: Open ethical dialogue

### Ethics Officers
- **Role and Responsibilities**: Program oversight
- **Independence**: Reporting relationships
- **Resources**: Adequate support and authority
- **Effectiveness Measurement**: Program evaluation`
        }
      },
      interactiveElements: [
        {
          type: 'finance_interactive',
          title: 'Ethics Program Designer',
          description: 'Design comprehensive ethics and compliance programs',
          exerciseData: {
            type: 'ethics_program',
            scenario: 'Corporate Ethics Program Development',
            components: ['code_of_ethics', 'training', 'reporting', 'monitoring']
          }
        }
      ]
    },
    {
      id: 'module-5',
      title: 'Contemporary Ethical Issues',
      week: 14,
      description: 'Technology ethics, privacy, artificial intelligence, and emerging ethical challenges',
      content: {
        type: 'lesson',
        data: {
          title: 'Technology Ethics and Emerging Challenges',
          content: `# Technology Ethics and Emerging Challenges

## Digital Ethics

### Data Privacy and Protection
- **Personal Data Rights**: Individual privacy protection
- **GDPR Compliance**: European data protection
- **Data Minimization**: Collecting only necessary data
- **Consent Management**: Informed user agreements

### Artificial Intelligence Ethics
- **Algorithmic Bias**: Fair and unbiased AI systems
- **Transparency**: Explainable AI decisions
- **Human Oversight**: Maintaining human control
- **Job Displacement**: Responsible automation

## Contemporary Challenges

### Social Media Ethics
- **Content Moderation**: Balancing free speech
- **Misinformation**: Preventing false information
- **User Manipulation**: Ethical design practices
- **Mental Health Impact**: Responsible platform design

### Environmental Technology
- **E-Waste Management**: Responsible disposal
- **Energy Consumption**: Sustainable technology
- **Green Computing**: Environmentally friendly IT
- **Sustainable Innovation**: Eco-conscious development`
        }
      },
      interactiveElements: [
        {
          type: 'finance_interactive',
          title: 'Technology Ethics Analyzer',
          description: 'Evaluate ethical implications of emerging technologies',
          exerciseData: {
            type: 'tech_ethics',
            scenario: 'AI Implementation Ethics Review',
            factors: ['privacy', 'bias', 'transparency', 'human_impact']
          }
        }
      ]
    }
  ],
  assignments: [
    {
      id: 'assignment-1',
      title: 'Ethical Dilemma Analysis',
      description: 'Analyze a real-world business ethical dilemma using multiple frameworks',
      week: 3,
      points: 150,
      requirements: [
        'Select a current business ethical controversy',
        'Apply three ethical frameworks to analyze the situation',
        'Identify all stakeholders and their interests',
        'Recommend ethical course of action with justification',
        'Present findings in structured report (8-10 pages)',
        'Include stakeholder impact analysis',
        'Cite minimum 8 academic and professional sources'
      ],
      rubric: {
        'Case Selection and Description': 25,
        'Ethical Framework Application': 40,
        'Stakeholder Analysis': 30,
        'Recommendations': 35,
        'Professional Writing': 20
      }
    },
    {
      id: 'assignment-2',
      title: 'Corporate Social Responsibility Strategy',
      description: 'Develop comprehensive CSR strategy for selected organization',
      week: 6,
      points: 175,
      requirements: [
        'Choose a real company for CSR strategy development',
        'Conduct stakeholder mapping and analysis',
        'Assess current CSR initiatives and gaps',
        'Design integrated CSR strategy with measurable goals',
        'Calculate cost-benefit analysis of proposed initiatives',
        'Create implementation timeline and metrics',
        'Present strategy to executive panel'
      ],
      rubric: {
        'Company Analysis': 35,
        'Stakeholder Mapping': 30,
        'Strategy Development': 45,
        'Financial Analysis': 35,
        'Implementation Plan': 30
      }
    },
    {
      id: 'assignment-3',
      title: 'Global Ethics Case Study',
      description: 'Analyze ethical challenges in international business operations',
      week: 9,
      points: 150,
      requirements: [
        'Select multinational corporation with global ethical issues',
        'Analyze cultural and legal differences across markets',
        'Identify ethical conflicts and compliance challenges',
        'Propose global ethics framework',
        'Address cultural sensitivity and universal standards',
        'Develop training recommendations for global teams'
      ],
      rubric: {
        'Global Context Analysis': 35,
        'Cultural Considerations': 30,
        'Ethics Framework Design': 40,
        'Training Recommendations': 25,
        'Professional Presentation': 20
      }
    },
    {
      id: 'assignment-4',
      title: 'Ethics Program Development',
      description: 'Design comprehensive organizational ethics and compliance program',
      week: 13,
      points: 175,
      requirements: [
        'Conduct organizational ethics risk assessment',
        'Design code of ethics for specific industry',
        'Develop ethics training curriculum',
        'Create reporting and investigation procedures',
        'Establish monitoring and evaluation systems',
        'Calculate program implementation costs',
        'Present program to organizational leadership'
      ],
      rubric: {
        'Risk Assessment': 30,
        'Code of Ethics Design': 35,
        'Training Program': 40,
        'Compliance Systems': 35,
        'Cost Analysis': 25,
        'Leadership Presentation': 10
      }
    }
  ],
  quizzes: [
    {
      id: 'quiz-1',
      title: 'Ethical Foundations Quiz',
      description: 'Assessment of ethical theories and decision-making frameworks',
      week: 2,
      points: 50,
      timeLimit: 45,
      questions: [
        {
          type: 'multiple_choice',
          question: 'Which ethical theory focuses on the consequences of actions?',
          options: ['Deontological', 'Virtue Ethics', 'Utilitarianism', 'Rights-based'],
          correct: 2,
          explanation: 'Utilitarianism judges actions based on their consequences and outcomes.'
        },
        {
          type: 'short_answer',
          question: 'Explain the difference between ethical relativism and ethical universalism.',
          points: 15,
          expectedLength: '4-5 sentences'
        }
      ]
    },
    {
      id: 'quiz-2',
      title: 'Corporate Social Responsibility Quiz',
      description: 'Assessment of CSR concepts and stakeholder management',
      week: 5,
      points: 50,
      timeLimit: 45,
      questions: [
        {
          type: 'multiple_choice',
          question: 'What does the triple bottom line approach emphasize?',
          options: ['Profit only', 'People, Planet, Profit', 'Shareholders first', 'Legal compliance'],
          correct: 1,
          explanation: 'Triple bottom line focuses on People, Planet, and Profit as balanced objectives.'
        }
      ]
    },
    {
      id: 'quiz-3',
      title: 'Global Business Ethics Quiz',
      description: 'Assessment of international ethics and cultural considerations',
      week: 8,
      points: 50,
      timeLimit: 45,
      questions: [
        {
          type: 'true_false',
          question: 'The Foreign Corrupt Practices Act applies only to US domestic business.',
          correct: false,
          explanation: 'FCPA applies to US companies and foreign companies operating in the US market.'
        }
      ]
    },
    {
      id: 'quiz-4',
      title: 'Ethics Programs and Compliance Quiz',
      description: 'Assessment of organizational ethics and compliance systems',
      week: 12,
      points: 50,
      timeLimit: 45,
      questions: [
        {
          type: 'multiple_choice',
          question: 'What is the primary purpose of an ethics hotline?',
          options: ['Performance reviews', 'Anonymous reporting', 'Customer service', 'Marketing research'],
          correct: 1,
          explanation: 'Ethics hotlines provide anonymous reporting mechanisms for ethical concerns.'
        }
      ]
    }
  ],
  finalExam: {
    id: 'final-exam',
    title: 'Comprehensive Business Ethics Examination',
    description: 'Assessment covering all business ethics concepts and applications',
    week: 16,
    points: 200,
    sections: [
      {
        name: 'Ethical Foundations',
        points: 50,
        topics: ['Ethical theories', 'Decision-making frameworks', 'Moral reasoning', 'Stakeholder analysis']
      },
      {
        name: 'Corporate Social Responsibility',
        points: 50,
        topics: ['CSR strategies', 'Stakeholder management', 'Sustainability', 'ESG integration']
      },
      {
        name: 'Global Business Ethics',
        points: 50,
        topics: ['International compliance', 'Cultural considerations', 'Global frameworks', 'Cross-cultural ethics']
      },
      {
        name: 'Contemporary Issues',
        points: 50,
        topics: ['Technology ethics', 'Digital privacy', 'AI ethics', 'Emerging challenges']
      }
    ]
  },
  finalProject: {
    id: 'final-project',
    title: 'Comprehensive Ethics Integration Project',
    description: 'Design and present complete ethical framework for organizational transformation',
    week: 15,
    points: 300,
    duration: '6 weeks',
    requirements: [
      'Select real organization facing ethical challenges',
      'Conduct comprehensive ethical assessment',
      'Design integrated ethics and CSR strategy',
      'Develop implementation plan with timeline',
      'Create training and communication programs',
      'Calculate costs and expected benefits',
      'Present recommendations to stakeholder panel',
      'Submit comprehensive written report (25-30 pages)',
      'Include risk assessment and mitigation strategies'
    ],
    deliverables: [
      {
        name: 'Project Proposal',
        week: 10,
        points: 25,
        description: 'Organization selection and ethical assessment plan'
      },
      {
        name: 'Ethical Assessment',
        week: 12,
        points: 50,
        description: 'Current state analysis and gap identification'
      },
      {
        name: 'Strategy Development',
        week: 14,
        points: 75,
        description: 'Comprehensive ethics and CSR strategy'
      },
      {
        name: 'Final Presentation',
        week: 15,
        points: 75,
        description: '20-minute stakeholder presentation'
      },
      {
        name: 'Written Report',
        week: 15,
        points: 75,
        description: 'Complete documentation and analysis'
      }
    ],
    rubric: {
      'Ethical Analysis': 60,
      'Strategic Thinking': 50,
      'Implementation Planning': 45,
      'Stakeholder Considerations': 55,
      'Professional Communication': 40,
      'Integration of Course Concepts': 50
    }
  },
  gradingBreakdown: {
    participation: { weight: 10, description: 'Class participation and discussion' },
    quizzes: { weight: 20, description: 'Four module quizzes' },
    assignments: { weight: 30, description: 'Four major assignments' },
    finalExam: { weight: 20, description: 'Comprehensive final examination' },
    finalProject: { weight: 20, description: 'Ethics integration project' }
  },
  gradingScale: {
    'A': '93-100%',
    'A-': '90-92%',
    'B+': '87-89%',
    'B': '83-86%',
    'B-': '80-82%',
    'C+': '77-79%',
    'C': '73-76%',
    'C-': '70-72%',
    'D+': '67-69%',
    'D': '60-66%',
    'F': 'Below 60%'
  },
  courseSchedule: {
    'Week 1': 'Ethical Foundations and Theories',
    'Week 2': 'Decision-Making Frameworks',
    'Week 3': 'Ethical Dilemma Assignment Due',
    'Week 4': 'Corporate Social Responsibility',
    'Week 5': 'Stakeholder Management',
    'Week 6': 'CSR Strategy Assignment Due',
    'Week 7': 'Global Business Ethics',
    'Week 8': 'Cultural Considerations',
    'Week 9': 'Global Ethics Case Study Due',
    'Week 10': 'Organizational Ethics / Project Proposal Due',
    'Week 11': 'Compliance Systems',
    'Week 12': 'Ethics Programs / Ethical Assessment Due',
    'Week 13': 'Ethics Program Assignment Due',
    'Week 14': 'Contemporary Issues / Strategy Due',
    'Week 15': 'Final Project Presentations',
    'Week 16': 'Final Examination'
  },
  coursePolicies: {
    attendance: {
      policy: 'Regular attendance essential for ethical discussion and case analysis.',
      makeupPolicy: 'Makeup opportunities require advance notice and valid documentation.'
    },
    lateSubmissions: {
      policy: 'Late assignments penalized 10% per day unless prior arrangements made.',
      exceptions: 'Extensions granted for documented emergencies or health issues.'
    },
    academicIntegrity: {
      policy: 'Ironclad commitment to academic honesty required in ethics course.',
      plagiarism: 'Plagiarism results in immediate course failure.',
      examPolicy: 'All exams individual efforts with no external assistance.'
    }
  },
  resources: [
    {
      type: 'textbook',
      title: 'Business Ethics: Ethical Decision Making and Cases',
      author: 'O.C. Ferrell, John Fraedrich, Linda Ferrell',
      edition: '12th Edition',
      isbn: '978-1337614436',
      required: true
    },
    {
      type: 'textbook',
      title: 'Ethics in Management',
      author: 'LaRue Tone Hosmer',
      edition: '8th Edition',
      required: false,
      purpose: 'Supplementary reading'
    },
    {
      type: 'case_studies',
      title: 'Harvard Business School Ethics Case Collection',
      access: 'University subscription',
      purpose: 'Real-world case analysis'
    },
    {
      type: 'journal',
      title: 'Journal of Business Ethics',
      access: 'University library',
      purpose: 'Current research and trends'
    },
    {
      type: 'website',
      title: 'Ethics and Compliance Initiative',
      url: 'https://www.ethics.org',
      purpose: 'Professional resources and best practices'
    },
    {
      type: 'database',
      title: 'Business Source Premier Ethics Collection',
      access: 'University library',
      purpose: 'Academic articles and research'
    }
  ]
};